import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    ConstructionObjectInput as BimConstructionObjectInput,
    BimWorkOrderOtherDistributionInput,
    BimWorkOrderOtherPriceData,
} from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { replacerCost, replacerCostIfValue } from 'utils/replacerCost';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    bimUpdateWorkOrderOtherDistributionGql,
    bimUpdateWorkOrderOtherDistributionToActKC2,
    bimWorkOrderOtherDistributionGql,
} from '../../BimWorkOrderOthersPrice/bimWorkOrderOthersQueries';

interface IUseBimMethodsDistributionOther {
    isOpenModal: boolean;
    dataRow: BimWorkOrderOtherPriceData | undefined;
    resetIncomingData: () => void;
    onCloseModal: () => void;
    statusWorkOrder: WorkOrderStatusEnum;
}

export const getConstructionObjectInputs = (objectDistribution: Record<string, any>[]) =>
    objectDistribution.reduce((acc: BimConstructionObjectInput[], distribution) => {
        const objectKey = Object.keys(distribution).find((k) => Number.isInteger(Number.parseInt(k, 10)));
        const cost = replacerCostIfValue(distribution.cost);

        if (objectKey && distribution[objectKey]) {
            // TODO:
            // @ts-ignore
            acc = [...acc, { id: Number(objectKey), cost }];
        }

        return acc;
    }, []);

export const getConstructionObjectInputsWOChange = (objectDistribution: Record<string, any>[]) =>
    objectDistribution.reduce((acc: BimConstructionObjectInput[], distribution) => {
        const objectKey = Object.keys(distribution).find((k) => k.split('_').length > 1);
        const cost = replacerCostIfValue(distribution.cost);

        if (objectKey && distribution[objectKey]) {
            // TODO:
            // @ts-ignore
            acc = [...acc, { id: objectKey, cost }];
        }

        return acc;
    }, []);

const requestId = 'bimUpdateWorkOrderOtherDistribution';

export const useBimMethodsDistributionOther = (props: IUseBimMethodsDistributionOther) => {
    const dispatch = useDispatch();

    const { isOpenModal, dataRow, resetIncomingData, onCloseModal, statusWorkOrder } = props;

    const variables = useMemo(
        () => ({
            workOrderOtherPriceId: dataRow?.id,
        }),
        [dataRow?.id],
    );

    const [bimWorkOrderOtherDistributionData, bimWorkOrderOtherDistributionStatus] = useRequest({
        requestId: 'bimWorkOrderOtherDistribution',
        query: bimWorkOrderOtherDistributionGql,
        variables,
        withEffect: isOpenModal && Boolean(dataRow?.id),
    });

    const [resetBimWorkOrderOtherDistribution] = useResetStoreData({
        requestId: 'bimWorkOrderOtherDistribution',
    });

    useEffect(
        () => () => {
            resetBimWorkOrderOtherDistribution(undefined);
        },
        [resetBimWorkOrderOtherDistribution],
    );

    const [
        bimUpdateWorkOrderOtherDistributionData,
        bimUpdateWorkOrderOtherDistributionStatus,
        ,
        bimUpdateWorkOrderOtherDistributionRequest,
    ] = useRequest({
        requestId,
        query: bimUpdateWorkOrderOtherDistributionGql,
        withEffect: false,
    });

    const [
        bimUpdateWorkOrderOtherDistributionForActData,
        bimUpdateWorkOrderOtherDistributionForActStatus,
        ,
        bimUpdateWorkOrderOtherDistributionForActRequest,
    ] = useRequest({
        requestId,
        query: bimUpdateWorkOrderOtherDistributionToActKC2,
        withEffect: false,
    });

    const [resetBimUpdateWorkOrderOtherDistribution] = useResetStoreData({
        requestId,
    });

    const isUpdateFetching = useMemo(
        () =>
            bimUpdateWorkOrderOtherDistributionStatus === RequestStatus.FETCHING ||
            bimUpdateWorkOrderOtherDistributionForActStatus === RequestStatus.FETCHING,
        [bimUpdateWorkOrderOtherDistributionForActStatus, bimUpdateWorkOrderOtherDistributionStatus],
    );

    const onUpdateOtherDistribution = useCallback(
        (data: {
            includedCost: string | number;
            objectDistribution: Record<string, any>[];
            isSystemCalculation: boolean;
        }) => {
            if (!dataRow) {
                return;
            }

            const objectList = getConstructionObjectInputs(data.objectDistribution);

            const workOrderOtherPriceId = dataRow.id;

            const input: BimWorkOrderOtherDistributionInput = {
                contractOtherCostId: dataRow.contractOtherCostId,
                includedCost: replacerCost(data.includedCost),
                isSystemCalculation: data.isSystemCalculation,
                // TODO:
                // @ts-ignore
                objectList,
            };

            if (statusWorkOrder === WorkOrderStatusEnum.InProgress) {
                dispatch(bimUpdateWorkOrderOtherDistributionForActRequest({ workOrderOtherPriceId, input }));
            } else {
                dispatch(
                    bimUpdateWorkOrderOtherDistributionRequest({
                        workOrderOtherPriceId: dataRow?.id,
                        input,
                    }),
                );
            }
        },
        [
            bimUpdateWorkOrderOtherDistributionForActRequest,
            bimUpdateWorkOrderOtherDistributionRequest,
            dataRow,
            dispatch,
            statusWorkOrder,
        ],
    );

    const resetDataDialog = useCallback(() => {
        onCloseModal();
        resetBimWorkOrderOtherDistribution(undefined);
    }, [onCloseModal, resetBimWorkOrderOtherDistribution]);

    useEffect(() => {
        if (
            !isUpdateFetching &&
            (bimUpdateWorkOrderOtherDistributionData?.bimUpdateWorkOrderOtherDistribution?.result ||
                bimUpdateWorkOrderOtherDistributionForActData?.bimUpdateWorkOrderOtherDistributionToActKC2?.result)
        ) {
            resetBimWorkOrderOtherDistribution(undefined);
            resetBimUpdateWorkOrderOtherDistribution(undefined);
            resetIncomingData();
        }
    }, [
        bimUpdateWorkOrderOtherDistributionData?.bimUpdateWorkOrderOtherDistribution?.result,
        bimUpdateWorkOrderOtherDistributionForActData?.bimUpdateWorkOrderOtherDistributionToActKC2?.result,
        isUpdateFetching,
        resetBimUpdateWorkOrderOtherDistribution,
        resetBimWorkOrderOtherDistribution,
        resetIncomingData,
    ]);

    return {
        bimWorkOrderOtherDistributionData: bimWorkOrderOtherDistributionData?.bimWorkOrderOtherDistribution,
        bimWorkOrderOtherDistributionStatus,
        resetBimWorkOrderOtherDistribution,
        bimUpdateWorkOrderOtherDistributionStatus,
        onUpdateOtherDistribution,
        resetDataDialog,
    };
};
